import { useState } from "react"
import { useOutletContext } from "react-router-dom"
import ToolShell from "../components/ToolShell"
import CTABox from "../components/CTABox"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"
import { ChevronDown, ChevronUp } from "lucide-react"

export default function CashFlowCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  // Income State
  const [showIncome, setShowIncome] = useState(true)
  const [monthlyRent, setMonthlyRent] = useState(1500)
  const [otherIncome, setOtherIncome] = useState(0)

  // Expense State
  const [showExpenses, setShowExpenses] = useState(true)
  const [mortgage, setMortgage] = useState(800)
  const [tax, setTax] = useState(150)
  const [insurance, setInsurance] = useState(50)
  const [hoa, setHoa] = useState(0)
  const [mgmt, setMgmt] = useState(150)
  const [maintenance, setMaintenance] = useState(100)
  const [vacancyRate, setVacancyRate] = useState(8)
  const [utilities, setUtilities] = useState(0)
  const [legal, setLegal] = useState(0)

  // CoC State
  const [showCoc, setShowCoc] = useState(false)
  const [cashInvested, setCashInvested] = useState(0)

  // Calculations
  const effectiveRent = monthlyRent * (1 - vacancyRate / 100)
  const totalIncome = effectiveRent + otherIncome
  const totalExpenses = mortgage + tax + insurance + hoa + mgmt + maintenance + utilities + legal
  const monthlyCashFlow = totalIncome - totalExpenses
  const annualCashFlow = monthlyCashFlow * 12
  const cocReturn = cashInvested > 0 ? (annualCashFlow / cashInvested) * 100 : null
  const breakEvenOccupancy = monthlyRent > 0 ? (totalExpenses / monthlyRent) * 100 : 0

  // Expense Breakdown for Bar Chart
  const expenseItems = [
    { label: "Mortgage", value: mortgage },
    { label: "Tax", value: tax },
    { label: "Insurance", value: insurance },
    { label: "Management", value: mgmt },
    { label: "Maintenance", value: maintenance },
    { label: "Other", value: hoa + utilities + legal },
  ].filter((item) => item.value > 0).sort((a, b) => b.value - a.value)

  return (
    <ToolShell>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 sm:p-8">
        
        {/* Input Form Column */}
        <div className="lg:col-span-7 space-y-6">
          
          {/* Section A: Income */}
          <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200">
            <button 
              onClick={() => setShowIncome(!showIncome)}
              className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors border-b border-slate-200"
            >
              <h3 className="font-semibold text-slate-900">Section A: Income</h3>
              {showIncome ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>
            {showIncome && (
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Rent</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={monthlyRent || ''} onChange={(e) => setMonthlyRent(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Other Income (Optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={otherIncome || ''} onChange={(e) => setOtherIncome(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" placeholder="Parking, laundry, pets..." />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section B: Expenses */}
          <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200">
            <button 
              onClick={() => setShowExpenses(!showExpenses)}
              className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors border-b border-slate-200"
            >
              <h3 className="font-semibold text-slate-900">Section B: Monthly Expenses</h3>
              {showExpenses ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>
            {showExpenses && (
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vacancy Allowance: {vacancyRate}%</label>
                  <input type="range" min="0" max="25" step="1" value={vacancyRate} onChange={(e) => setVacancyRate(Number(e.target.value))} className="w-full accent-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Mortgage Payment</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={mortgage || ''} onChange={(e) => setMortgage(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property Tax</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={tax || ''} onChange={(e) => setTax(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Insurance</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={insurance || ''} onChange={(e) => setInsurance(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">HOA / Service Charge</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={hoa || ''} onChange={(e) => setHoa(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Management Fee</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={mgmt || ''} onChange={(e) => setMgmt(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Maintenance Reserve</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={maintenance || ''} onChange={(e) => setMaintenance(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Hint: ~1% of property value ÷ 12</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Utilities</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={utilities || ''} onChange={(e) => setUtilities(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Accountancy / Legal</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                    <input type="number" value={legal || ''} onChange={(e) => setLegal(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section C: Cash-on-Cash */}
          <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200">
            <button 
              onClick={() => setShowCoc(!showCoc)}
              className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors border-b border-slate-200"
            >
              <h3 className="font-semibold text-slate-900">Section C: Cash Invested (Optional)</h3>
              {showCoc ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>
            {showCoc && (
              <div className="p-5">
                <label className="block text-sm font-medium text-slate-700 mb-1">Total Cash Invested</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={cashInvested || ''} onChange={(e) => setCashInvested(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" placeholder="Down payment + costs + repairs" />
                </div>
                <p className="text-xs text-slate-500 mt-1 border-t border-slate-200 pt-3 mt-3">
                  Enter your total upfront cash to calculate Cash-on-Cash (CoC) return. 
                </p>
              </div>
            )}
          </div>

        </div>

        {/* Results Column */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white border text-center border-slate-200 p-6 rounded-2xl shadow-sm">
            <div className="text-sm font-medium text-slate-500 mb-2">Monthly Cash Flow</div>
            <div className={`text-5xl font-extrabold tracking-tight ${monthlyCashFlow >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {monthlyCashFlow >= 0 ? '+' : ''}{currency}{monthlyCashFlow.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center text-sm">
              <span className="text-slate-500">Annual Cash Flow</span>
              <span className="font-bold text-slate-900">{currency}{annualCashFlow.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
            </div>
            
            {cocReturn !== null && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center text-sm">
                <span className="text-slate-500">Cash-on-Cash Return</span>
                <span className="font-bold text-primary">{cocReturn.toFixed(2)}%</span>
              </div>
            )}
            
            <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center text-sm">
              <span className="text-slate-500">Break-even Occupancy</span>
              <span className="font-bold text-amber-600">{breakEvenOccupancy.toFixed(1)}%</span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
            <h4 className="font-semibold text-slate-900 mb-4">Expense Breakdown</h4>
            <div className="space-y-3">
              {expenseItems.map((item, idx) => {
                const pct = totalExpenses > 0 ? (item.value / totalExpenses) * 100 : 0
                return (
                  <div key={idx} className="flex flex-col gap-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">{item.label}</span>
                      <span className="font-medium text-slate-900">{currency}{item.value} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden flex">
                      <div className="h-full bg-slate-400" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
              {expenseItems.length === 0 && (
                <div className="text-slate-500 text-sm py-2">No expenses added yet.</div>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-6 pt-4 border-t border-slate-100 italic">
              Note: Positive cash flow ≠ total profit. Equity paydown through your mortgage and property appreciation add to your total hidden returns!
            </p>
          </div>
        </div>
      </div>

      {/* Shared Footer Elements */}
      <div className="px-6 sm:px-8 pb-10">
        <FormulaBox 
          formula={`Annual Cash Flow = (Monthly Rent × (1 - Vacancy Rate) + Other Income - Total Monthly Expenses) × 12\n\nCash-on-Cash Return = (Annual Cash Flow / Total Cash Invested) × 100`} 
        />
        
        <CTABox 
          icon={tool.icon} 
          heading="Track this automatically" 
          body={tool.ctaText} 
          ctaLabel="Use RentKeep Free" 
          ctaHref="/register" 
        />
        
        <ToolFAQ 
          items={[
            { q: "What is a good cash flow for rental property?", a: "A 'good' cash flow depends heavily on your local market and strategy. Many investors aim for at least {currency}100-{currency}300 per door per month in net positive cash flow, though high-appreciation markets often see lower initial yields." },
            { q: "What does break-even occupancy mean?", a: "This represents how much of your gross possible rent you need to collect just to cover your monthly operating expenses. If your break-even is 75%, it means your property must be occupied and paying rent 75% of the year to not lose money." },
            { q: "Is principal paydown considered cash flow?", a: "No. Your mortgage principal payment is essentially transferring cash into equity (wealth). However, since that cash leaves your bank account each month, it is an expense in the strictly 'cash flow' sense." }
          ]} 
        />
        
        <RelatedToolsList relatedSlugs={tool.related} />
      </div>
    </ToolShell>
  )
}
